import React, { createContext, useContext, useState, useCallback } from 'react';

export interface RailButton {
  id: string;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  order?: number;
}

interface RightRailContextValue {
  buttons: RailButton[];
  activePanelId: string | null;
  registerButton: (button: RailButton) => void;
  updateButton: (id: string, updates: Partial<RailButton>) => void;
  unregisterButton: (id: string) => void;
  togglePanel: (id: string) => void;
  closePanel: () => void;
}

const RightRailContext = createContext<RightRailContextValue | null>(null);

export const RightRailProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [buttons, setButtons] = useState<RailButton[]>([]);
  const [activePanelId, setActivePanelId] = useState<string | null>(null);

  const registerButton = useCallback((button: RailButton) => {
    setButtons(prev => {
      const exists = prev.find(b => b.id === button.id);
      if (exists) return prev.map(b => b.id === button.id ? { ...b, ...button } : b);
      return [...prev, button].sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
    });
  }, []);

  const updateButton = useCallback((id: string, updates: Partial<RailButton>) => {
    setButtons(prev => prev.map(b => b.id === id ? { ...b, ...updates } : b));
  }, []);

  const unregisterButton = useCallback((id: string) => {
    setButtons(prev => prev.filter(b => b.id !== id));
    setActivePanelId(prev => prev === id ? null : prev);
  }, []);

  const togglePanel = useCallback((id: string) => {
    setActivePanelId(prev => prev === id ? null : id);
  }, []);

  const closePanel = useCallback(() => setActivePanelId(null), []);

  return (
    <RightRailContext.Provider value={{
      buttons, activePanelId,
      registerButton, updateButton, unregisterButton,
      togglePanel, closePanel,
    }}>
      {children}
    </RightRailContext.Provider>
  );
};

export const useRightRail = () => {
  const ctx = useContext(RightRailContext);
  if (!ctx) throw new Error('useRightRail must be used inside RightRailProvider');
  return ctx;
};
